import { buildNode, nativeEntryFieldTypes } from "@autodromo/schema-catalog";
import type { Thing } from "schema-dts";
import type { Brand } from "../../brands";
import { buildEntryUrl } from "../../entryUrl";
import type { ChannelUnit } from "../../s3";
import { dealerId, entryId } from "../helpers/ids";
import { relationFieldValues } from "../helpers/relationFieldValues";
import { resolveUnitFieldValues } from "../helpers/unitFieldValues";
import type { SchemaBuilder, StructuredDataContext } from "../types";

function fieldTypesOf(item: Record<string, any>): Record<string, string> {
  const fields: Array<{ name: string; type: string }> =
    item?._metadata?.fields ?? [];

  return {
    ...nativeEntryFieldTypes,
    ...Object.fromEntries(fields.map((field) => [field.name, field.type])),
  };
}

function buildUnitResolver(units: ChannelUnit[]) {
  const byId = new Map(units.map((unit) => [unit.id, unit]));

  return (id: string) => byId.get(id) ?? null;
}

function buildBrandResolver(brands: Brand[]) {
  const byId = new Map(brands.map((brand) => [brand.id, brand]));

  return (id: string) => byId.get(id) ?? null;
}

/**
 * Um nó por registro exibido na página, montado a partir da configuração do tipo
 * de conteúdo. Vale igual para a página de detalhe e para os blocos dinâmicos —
 * o que muda é quantos itens cada fonte traz.
 *
 * Registro sem `_metadata.id` não vira nó: sem id não há `@id` estável, e o
 * critério de identidade é inegociável.
 */
export const buildCmsEntries: SchemaBuilder = (
  context: StructuredDataContext,
) => {
  const resolveUnit = buildUnitResolver(context.units);
  const resolveBrand = buildBrandResolver(context.brands);

  return context.entrySources.flatMap(({ config, items, listingLink }) =>
    items.flatMap((item): Thing[] => {
      const id = item?._metadata?.id;
      if (typeof id !== "string" || id.length === 0) return [];

      // Campo de tipo relacionado entra sob a chave composta que o mapeamento
      // usa. Os do próprio registro vêm depois e vencem: o nome composto não
      // colide com nome de campo da coleção, mas a precedência fica explícita.
      const related = relationFieldValues({ item, mappings: config.mappings });
      const fieldTypes = { ...related.fieldTypes, ...fieldTypesOf(item) };

      const node = buildNode({
        config,
        values: resolveUnitFieldValues({
          values: { ...related.values, ...item },
          fieldTypes,
          resolveUnit,
        }),
        fieldTypes,
        resolveUnit,
        resolveBrand,
        unitReferenceId: (unit) => dealerId(unit.publicId || unit.id),
      });

      if (!node) return [];

      const { "@type": type, ...properties } = node;

      // Sem link configurado no bloco não há como saber o endereço do registro,
      // e a propriedade fica de fora. O `omitEmpty` cuida do `undefined`.
      const url = buildEntryUrl({
        baseUrl: context.baseUrl,
        listingLink,
        itemSlug: item?.slug,
      });

      return [
        {
          "@type": type,
          "@id": entryId(id),
          ...(url ? { url } : {}),
          ...properties,
        } as unknown as Thing,
      ];
    }),
  );
};
